import {
  Table,
  TableHead,
  TableHeaderCell,
  TableBody,
  TableRow,
  TableCell,
} from '@tremor/react';
import { TextInput } from '@tremor/react';
import { setModalOpen } from '../campaigns/campaignsSlice';
import { useAppDispatch } from '../../app/hooks';
import ModalDeleteUser from '../campaigns/modals/ModalDeleteUser';
import { useNavigate } from 'react-router-dom';
import { useEffect, useState } from 'react';
import { useProfiles, Profile } from '../../hooks/useProfiles';

const UsersTable = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { profiles, loading, error, fetchProfiles } = useProfiles();
  const [filteredProfiles, setFilteredProfiles] = useState<Profile[]>([]);
  const [userFilter, setUserFilter] = useState('');
  const [sortField, setSortField] = useState<'full_name' | 'email' | 'role'>('full_name');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc');

  const _url = new URL(window.location.href);
  const _user = _url.searchParams.get('user');
  const currentUserEmail = _user || '';

  useEffect(() => {
    fetchProfiles();
    if (currentUserEmail) {
      setUserFilter(currentUserEmail);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentUserEmail]);

  useEffect(() => {
    let filtered = profiles.filter(profile =>
      profile.full_name.toLowerCase().includes(userFilter.toLowerCase()) ||
      profile.email.toLowerCase().includes(userFilter.toLowerCase())
    );

    // Sort profiles
    filtered.sort((a, b) => {
      let aValue = a[sortField];
      let bValue = b[sortField];
      
      if (sortField === 'role') {
        aValue = aValue === 'admin' ? 'Admin' : 'Agente';
        bValue = bValue === 'admin' ? 'Admin' : 'Agente';
      }

      if (aValue < bValue) return sortDirection === 'asc' ? -1 : 1;
      if (aValue > bValue) return sortDirection === 'asc' ? 1 : -1;
      return 0;
    });

    setFilteredProfiles(filtered);
  }, [profiles, userFilter, sortField, sortDirection]);

  const handleUserOnClick = (id: string) => {
    navigate('/admin/utenze/modifica-utente/' + id);
  };

  const handleSrc = (e: React.FormEvent<HTMLInputElement>) => {
    setUserFilter(e.currentTarget.value.toLowerCase());
  };

  const handleSort = (field: 'full_name' | 'email' | 'role') => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const handleDeleteUser = (profile: Profile) => {
    dispatch(
      setModalOpen(
        <ModalDeleteUser
          userName={profile.full_name}
          userEmail={profile.email}
          userId={profile.id}
        />,
      ),
    );
  };

  if (loading) {
    return (
      <div className="pzo__table-route campaigns__table users__table bradius-bshadow bg-transparent-white">
        <div style={{ textAlign: 'center', padding: '2rem' }}>
          Caricamento utenti...
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="pzo__table-route campaigns__table users__table bradius-bshadow bg-transparent-white">
        <div style={{ textAlign: 'center', padding: '2rem', color: 'red' }}>
          Errore: {error}
        </div>
      </div>
    );
  }

  return (
    <div className="pzo__table-route campaigns__table users__table bradius-bshadow bg-transparent-white">
      <div className="pzo__table-filter">
        <TextInput
          placeholder="Filtra utenti..."
          onChange={handleSrc}
          value={userFilter}
        />
      </div>
      <Table marginTop="mt-0">
        <TableHead>
          <TableRow>
            <TableHeaderCell>
              <span
                className="table__head"
                onClick={() => handleSort('full_name')}
                style={{ cursor: 'pointer' }}
              >
                Nome
                {sortField === 'full_name' && (
                  <span style={{ marginLeft: '5px' }}>
                    {sortDirection === 'asc' ? '↑' : '↓'}
                  </span>
                )}
              </span>
            </TableHeaderCell>
            <TableHeaderCell>
              <span
                className="table__head"
                onClick={() => handleSort('email')}
                style={{ cursor: 'pointer' }}
              >
                Email
                {sortField === 'email' && (
                  <span style={{ marginLeft: '5px' }}>
                    {sortDirection === 'asc' ? '↑' : '↓'}
                  </span>
                )}
              </span>
            </TableHeaderCell>
            <TableHeaderCell>
              <span
                className="table__head"
                onClick={() => handleSort('role')}
                style={{ cursor: 'pointer' }}
              >
                Ruolo
                {sortField === 'role' && (
                  <span style={{ marginLeft: '5px' }}>
                    {sortDirection === 'asc' ? '↑' : '↓'}
                  </span>
                )}
              </span>
            </TableHeaderCell>
            <TableHeaderCell>
              <span>Stato</span>
            </TableHeaderCell>
            <TableHeaderCell>
              <></>
            </TableHeaderCell>
            <TableHeaderCell>
              <></>
            </TableHeaderCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {filteredProfiles.map((profile) => {
            return (
              <TableRow key={profile.id}>
                <TableCell>
                  <span className="min-w">{profile.full_name}</span>
                </TableCell>

                <TableCell>
                  <span className="min-w">{profile.email}</span>
                </TableCell>

                <TableCell>
                  <span className="min-w">
                    {profile.role === 'agent' ? 'Agente' : 'Admin'}
                  </span>
                </TableCell>

                <TableCell>
                  <span className="min-w">
                    {(() => {
                      if (profile.disabled) {
                        return <strong style={{ color: 'var(--red)' }}>Disabilitato</strong>;
                      }
                      
                      const isVerified = profile.email_confirmed_at !== null;
                      
                      if (isVerified) {
                        return <strong style={{ color: 'var(--green)' }}>Attivo</strong>;
                      } else {
                        return <strong style={{ color: '#9ca1a9' }}>Da verificare</strong>;
                      }
                    })()}
                  </span>
                </TableCell>

                <TableCell>
                  <span
                    className="edit-user"
                    onClick={() => handleUserOnClick(profile.id)}
                  >
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 24 24"
                      strokeWidth={1.5}
                      stroke="#6a163b"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        d="M16.862 4.487l1.687-1.688a1.875 1.875 0 112.652 2.652L10.582 16.07a4.5 4.5 0 01-1.897 1.13L6 18l.8-2.685a4.5 4.5 0 011.13-1.897l8.932-8.931zm0 0L19.5 7.125M18 14v4.75A2.25 2.25 0 0115.75 21H5.25A2.25 2.25 0 013 18.75V8.25A2.25 2.25 0 015.25 6H10"
                      />
                    </svg>
                    Modifica
                  </span>
                </TableCell>

                <TableCell>
                  <span
                    className="delete-user"
                    onClick={() => console.log('elimina utente')}
                  >
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 24 24"
                      strokeWidth={1.5}
                      stroke="#FF632F"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        d="M14.74 9l-.346 9m-4.788 0L9.26 9m9.968-3.21c.342.052.682.107 1.022.166m-1.022-.165L18.16 19.673a2.25 2.25 0 01-2.244 2.077H8.084a2.25 2.25 0 01-2.244-2.077L4.772 5.79m14.456 0a48.108 48.108 0 00-3.478-.397m-12 .562c.34-.059.68-.114 1.022-.165m0 0a48.11 48.11 0 013.478-.397m7.5 0v-.916c0-1.18-.91-2.164-2.09-2.201a51.964 51.964 0 00-3.32 0c-1.18.037-2.09 1.022-2.09 2.201v.916m7.5 0a48.667 48.667 0 00-7.5 0"
                      />
                    </svg>
                    Elimina
                  </span>
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </div>
  );
};

export default UsersTable;
